import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, resolveComponent as _resolveComponent, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["onSubmit"]
const _hoisted_2 = { class: "form-group" }
const _hoisted_3 = { class: "faux-label" }
const _hoisted_4 = { class: "faux-input" }
const _hoisted_5 = {
  key: 0,
  class: "form-group"
}
const _hoisted_6 = { class: "faux-label" }
const _hoisted_7 = { class: "faux-input" }
const _hoisted_8 = { class: "form-group" }
const _hoisted_9 = { class: "faux-label" }
const _hoisted_10 = { class: "faux-input" }
const _hoisted_11 = { value: "" }
const _hoisted_12 = ["value"]
const _hoisted_13 = { value: "" }
const _hoisted_14 = ["value"]
const _hoisted_15 = { value: "" }
const _hoisted_16 = ["value"]

import { computed, onBeforeMount, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { AppStore } from '@/store';
import { useGettext } from '@jshmrtn/vue3-gettext';
import { ApiError } from '@/core/http/ApiError';
import auth from '@/core/Auth';
import { getValueInUserLanguage } from '@/core/languageContent';
import { ErrorBag, handleFormError } from '@/core/common';
import { Flash } from '@/core/Flash';
import AppFormGroup from '@/core/components/form/AppFormGroup.vue';
import AppSelect from '@/core/components/form/AppSelect.vue';
import AppButton from '@/core/components/form/AppButton.vue';
import { addedViaNames, Ticket, useTicket } from '@/modules/ticket/Ticket';
import { TicketStatus } from '@/modules/ticket_status/TicketStatus';
import { TicketCategory } from '@/modules/ticket_category/TicketCategory';
import { User, useUser } from '@/modules/user/User';
import { CustomerLawFirm, useCustomer } from '@/modules/customer/Customer';
import { CustomerUserResource, useCustomerUser } from '@/modules/customer/CustomerUser';

interface TicketViewDetailsFormProps {
	ticket: Ticket;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'TicketViewDetailsForm',
  props: {
    ticket: {}
  },
  emits: [
	'saved',
],
  setup(__props: any, { emit: __emit }) {

const { $gettext } = useGettext();
const props = __props;
const emit = __emit;
const resource = useTicket();
const userResource = useUser();
const customerResource = useCustomer();
let customerUserResource: CustomerUserResource | null = null;
const store = useStore<AppStore>();
const status = ref<number | string>('');
const lawFirm = ref<number | string>('');
const worker = ref<number | string>('');
const ticketStatusOptions = computed<TicketStatus[]>(() => store.getters.ticketStatuses);
const ticketCategoryOptions = computed<TicketCategory[]>(() => store.getters.ticketCategories);
const lawFirmOptions = ref<CustomerLawFirm[]>([]);
const lawFirmUsers = ref<User[]>([]);
const customerUsers = ref<User[]>([]);
const errors = ref<ErrorBag>({});
const canAssign = computed<boolean>(() => auth.can('TICKET_ASSIGN'));
const canAssignAnonymous = computed<boolean>(() => {
	const customer = store.state.authAssociatedCustomer;
	if (!customer) {
		return false;
	}

	if (customer.law_firm_mode === 'CUSTOMER' && worker.value) {
		return true;
	}

	return (customer.law_firm_mode === 'CUSTOMER_FIRST');
});
const canAssignLawFirm = ref<boolean>(false);
const authUser = computed<User>(() => store.state.authenticatedUser as User);
const isLawFirmUser = computed<boolean>(() => !!authUser.value.law_firm);

const visibleUserOptions = computed<User[]>(() => {
	if (canAssignLawFirm.value) {
		if (lawFirm.value === '') {
			return customerUsers.value;
		}

		if (lawFirm.value !== authUser.value.law_firm.id) {
			return [];
		}
	}

	if (!authUser.value.law_firm) {
		return customerUsers.value;
	}

	return lawFirmUsers.value;
});

watch(lawFirm, () => {
	if (!canAssignLawFirm.value) {
		return;
	}

	worker.value = '';
});

const setData = () => {
	status.value = props.ticket.status?.id || '';
	lawFirm.value = props.ticket.law_firm?.id || '';
	worker.value = props.ticket.worker?.id || '';

	customerUserResource = useCustomerUser(props.ticket.customer.id);
};

onBeforeMount(async () => {
	setData();

	await store.dispatch('loadTicketStatuses');
	await store.dispatch('loadTicketCategories');

	if (!authUser.value.law_firm) {
		if (canAssign.value && customerUserResource) {
			customerUsers.value = await userResource.getAllNoPagination();
		}

		return;
	}

	lawFirmUsers.value = await userResource.getAllNoPagination();

	try {
		lawFirmOptions.value = await customerResource.getLawFirms(props.ticket.customer.id);
		canAssignLawFirm.value = lawFirmOptions.value.reduce(
			(carry: boolean, customerLawFirm: CustomerLawFirm): boolean => {
				return (customerLawFirm.can_view_all_tickets ? true : carry);
			},
			false,
		);
	} catch (e) {
		if (!(e as ApiError).response || (e as ApiError).response.status !== 403) {
			throw e;
		}
	}

	if (canAssignLawFirm.value && customerUserResource) {
		customerUsers.value = await customerUserResource.getAllNoPagination();
	}
});
watch(() => props, setData, { deep: true });

const addedVia = computed(() => {
	return addedViaNames[props.ticket.added_via];
});

const categoryName = computed(() => {
	const option = ticketCategoryOptions.value.find(tc => tc.id === props.ticket.category?.id)?.content;
	if (!option) {
		return '-';
	}

	return getValueInUserLanguage(option);
});

const save = async () => {
	errors.value = {};

	try {
		let ticket;
		const newStatus: number | null = (status.value === '' ? null : parseInt(status.value as string, 10));
		const newLawFirm: number | null = (lawFirm.value === '' ? null : parseInt(lawFirm.value as string, 10));
		const newWorker: number | null = (worker.value === '' ? null : parseInt(worker.value as string, 10));

		if (newStatus !== (props.ticket.status?.id || null)) {
			ticket = await resource.putStatus(props.ticket.id, newStatus);
			Flash.success('Statusen uppdaterades!');
		}

		if (canAssign.value && canAssignLawFirm && newLawFirm !== (props.ticket.law_firm?.id || null)) {
			ticket = await resource.assignLawFirm(props.ticket.id, newLawFirm);
			Flash.success('Tilldelning av ärendet till extern part lyckades!');
		}

		if (canAssign.value && newWorker !== (props.ticket.worker?.id || null)) {
			ticket = await resource.assignWorker(props.ticket.id, newWorker);
			Flash.success('Ändring av handläggare sparades!');
		}

		if (ticket) {
			emit('saved', ticket);
		}
	} catch (e) {
		handleFormError(e as Error, errors);
	}
};

const assignAnonymousLawFirm = async () => {
	errors.value = {};

	if (!canAssignAnonymous.value) {
		return;
	}

	try {
		const ticket = await resource.unAssignWorker(props.ticket.id);
		Flash.success('Tilldelning av ärendet till extern part lyckades!');

		emit('saved', ticket);
	} catch (e) {
		handleFormError(e as Error, errors);
	}
};

return (_ctx: any,_cache: any) => {
  const _component_translate = _resolveComponent("translate")!
  const _directive_translate = _resolveDirective("translate")!

  return (_openBlock(), _createElementBlock("form", {
    onSubmit: _withModifiers(save, ["prevent"])
  }, [
    _createElementVNode("div", _hoisted_2, [
      _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_3, [
        _createTextVNode("Inkom via")
      ])), [
        [_directive_translate]
      ]),
      _createElementVNode("span", _hoisted_4, _toDisplayString(addedVia.value), 1)
    ]),
    (isLawFirmUser.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_6, [
            _createTextVNode("Kund")
          ])), [
            [_directive_translate]
          ]),
          _createElementVNode("span", _hoisted_7, _toDisplayString(props.ticket.customer.name), 1)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_8, [
      _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_9, [
        _createTextVNode("Kategori")
      ])), [
        [_directive_translate]
      ]),
      _createElementVNode("span", _hoisted_10, _toDisplayString(categoryName.value), 1)
    ]),
    _createVNode(AppFormGroup, {
      id: "ticket_details_status",
      errors: errors.value.status,
      label: _unref($gettext)('Status')
    }, {
      default: _withCtx(() => [
        _createVNode(AppSelect, {
          id: "ticket_details_status",
          modelValue: status.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((status).value = $event))
        }, {
          default: _withCtx(() => [
            _withDirectives((_openBlock(), _createElementBlock("option", _hoisted_11, [
              _createTextVNode("(Ej vald)")
            ])), [
              [_directive_translate]
            ]),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(ticketStatusOptions.value, (ticketStatus) => {
              return (_openBlock(), _createElementBlock("option", {
                key: ticketStatus.id,
                value: ticketStatus.id
              }, _toDisplayString(_unref(getValueInUserLanguage)(ticketStatus.content)), 9, _hoisted_12))
            }), 128))
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _: 1
    }, 8, ["errors", "label"]),
    (canAssignLawFirm.value)
      ? (_openBlock(), _createBlock(AppFormGroup, {
          key: 1,
          id: "ticket_details_law_firm",
          errors: errors.value.law_firm,
          label: _unref($gettext)('Extern part')
        }, {
          default: _withCtx(() => [
            _createVNode(AppSelect, {
              id: "ticket_details_law_firm",
              modelValue: lawFirm.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((lawFirm).value = $event))
            }, {
              default: _withCtx(() => [
                _withDirectives((_openBlock(), _createElementBlock("option", _hoisted_13, [
                  _createTextVNode("(Kunden)")
                ])), [
                  [_directive_translate]
                ]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(lawFirmOptions.value, (customerLawFirm) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: customerLawFirm.law_firm.id,
                    value: customerLawFirm.law_firm.id
                  }, _toDisplayString(customerLawFirm.law_firm.name), 9, _hoisted_14))
                }), 128))
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _: 1
        }, 8, ["errors", "label"]))
      : _createCommentVNode("", true),
    (canAssign.value)
      ? (_openBlock(), _createBlock(AppFormGroup, {
          key: 2,
          id: "ticket_details_worker",
          errors: errors.value.worker,
          label: _unref($gettext)('Handläggare')
        }, {
          default: _withCtx(() => [
            _createVNode(AppSelect, {
              id: "ticket_details_worker",
              modelValue: worker.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((worker).value = $event)),
              disabled: !canAssign.value
            }, {
              default: _withCtx(() => [
                _withDirectives((_openBlock(), _createElementBlock("option", _hoisted_15, [
                  _createTextVNode("(Ej vald)")
                ])), [
                  [_directive_translate]
                ]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleUserOptions.value, (user) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: user.id,
                    value: user.id
                  }, _toDisplayString(user.name), 9, _hoisted_16))
                }), 128))
              ]),
              _: 1
            }, 8, ["modelValue", "disabled"])
          ]),
          _: 1
        }, 8, ["errors", "label"]))
      : _createCommentVNode("", true),
    _createElementVNode("p", null, [
      (canAssignAnonymous.value)
        ? (_openBlock(), _createBlock(AppButton, {
            key: 0,
            class: "t-primary",
            onClick: _withModifiers(assignAnonymousLawFirm, ["prevent"])
          }, {
            default: _withCtx(() => [
              _createVNode(_component_translate, null, {
                default: _withCtx(() => [
                  _createTextVNode(" Tilldela extern part ")
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["onClick"]))
        : _createCommentVNode("", true),
      _createVNode(AppButton, {
        type: "submit",
        class: "t-primary"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_translate, null, {
            default: _withCtx(() => [
              _createTextVNode("Uppdatera")
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ])
  ], 40, _hoisted_1))
}
}

})